import { Link, NavLink, useNavigate } from "react-router-dom";
import { ArrowLeft, Heart, Users, User, Home, Video, LogOut } from "lucide-react";

import { signOut } from "@/lib/auth";
import { MobileBottomNav } from "@/components/layout/MobileBottomNav";

interface Props {
  /** Current event slug — drives the in-event nav block. */
  slug: string;
  /** Show "(N)" badge on Matches when there are mutual connections. */
  matchesCount?: number;
}

/**
 * Sidebar shown on every /e/:slug/* page when the visitor is authenticated.
 *
 * Layout intent:
 *   - keep the same chrome the user already sees on /me/* pages
 *     (left rail + topbar) so clicking "View matches" inside an event
 *     doesn't feel like falling into a different app
 *   - top block: this event's tabs (Feed / Matches / Profile)
 *   - middle block: cross-event shortcuts (My events / My matches / My pitch)
 *   - bottom: account + sign out
 *
 * Anonymous participants (no Supabase session) still get the legacy
 * mobile-first ParticipantShell with a bottom tab bar — see ParticipantShell.
 */
export function EventParticipantSidebar({ slug, matchesCount }: Props) {
  const navigate = useNavigate();
  const base = `/e/${slug}`;

  const onSignOut = async () => {
    await signOut();
    navigate("/", { replace: true });
  };

  return (
    <>
    <aside className="hidden border-r border-[#e2e8f0] bg-white px-5 py-8 md:sticky md:top-16 md:flex md:h-[calc(100vh-64px)] md:flex-col md:gap-1 md:self-start md:overflow-y-auto">
      <Link
        to="/me/meetups"
        className="mb-3 inline-flex items-center gap-2 text-[12px] font-semibold text-[#2563eb] no-underline hover:text-[#1d4ed8]"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        All my events
      </Link>

      <p className="mb-2 mt-1 px-3 text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
        This event
      </p>
      <Item to={`${base}/feed`} label="Feed" icon={Heart} />
      <Item
        to={`${base}/matches`}
        label={matchesCount && matchesCount > 0 ? `Matches (${matchesCount})` : "Matches"}
        icon={Users}
      />
      <Item to={`${base}/profile`} label="My pitch" icon={Video} />

      <p className="mb-2 mt-5 px-3 text-[11px] font-semibold uppercase tracking-wide text-[#94a3b8]">
        Account
      </p>
      <Item to="/me/meetups" label="My events" icon={Home} end={false} />
      <Item to="/me/matches" label="All my matches" icon={Heart} end={false} />
      <Item to="/me/account" label="Profile" icon={User} end={false} />
      <button
        type="button"
        onClick={onSignOut}
        className="mt-1 flex items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-left text-[14px] font-medium text-[#475569] transition hover:bg-[#f8fafc] hover:text-[#1a1a1a]"
      >
        <LogOut className="h-4 w-4 opacity-70" />
        Sign out
      </button>
    </aside>
    {/* Mobile bottom nav for the event context. Keeps the four primary
        in-event destinations one tap away when the sidebar is hidden. */}
    <MobileBottomNav
      items={[
        { to: `${base}/feed`, label: "Feed", icon: Heart },
        {
          to: `${base}/matches`,
          label: matchesCount && matchesCount > 0 ? `Matches (${matchesCount})` : "Matches",
          icon: Users,
        },
        { to: `${base}/profile`, label: "My pitch", icon: Video },
        { to: "/me/meetups", label: "All events", icon: Home, end: false },
      ]}
      showSignOut={false}
    />
    </>
  );
}

function Item({
  to,
  label,
  icon: Icon,
  end = true,
}: {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-2.5 rounded-[10px] px-3 py-2.5 text-[14px] transition no-underline ${
          isActive
            ? "bg-[#eff6ff] font-semibold text-[#2563eb]"
            : "font-medium text-[#475569] hover:bg-[#f8fafc] hover:text-[#1a1a1a]"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <Icon className={`h-4 w-4 ${isActive ? "" : "opacity-70"}`} />
          {label}
        </>
      )}
    </NavLink>
  );
}
